import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { getApiUrl } from '../config/apiConfig';
import socketService from '../services/socketService';
import { getShortName } from '../utils/userDisplay';
// eslint-disable-next-line no-unused-vars
import { getAuthenticatedImageUrl } from '../utils/imageUtils';
import { getProfilePicUrl } from '../utils/urlHelper';
import './Sidebar.css';

const Sidebar = ({ isCollapsed, onToggle, isPinned: propIsPinned, onPinChange }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userRole, setUserRole] = useState(null); // User's role
  const [userStatus, setUserStatus] = useState('active'); // Default to active
  const [localIsPinned, setLocalIsPinned] = useState(false); // Local pin state
  const [unreadCount, setUnreadCount] = useState(null); // Unread message count
  const navigate = useNavigate();

  // Use prop if provided, otherwise use local state
  const isPinned = propIsPinned !== undefined ? propIsPinned : localIsPinned;
  const handlePinToggle = onPinChange || setLocalIsPinned;

  // Load pin state from localStorage on mount (only if not controlled by props)
  useEffect(() => {
    if (propIsPinned === undefined) {
      const savedPinState = localStorage.getItem('sidebarPinned');
      if (savedPinState !== null) {
        setLocalIsPinned(savedPinState === 'true');
      }
    }
  }, [propIsPinned]);

  // Save pin state to localStorage when it changes (only for local state)
  useEffect(() => {
    if (propIsPinned === undefined) {
      localStorage.setItem('sidebarPinned', isPinned.toString());
    }
  }, [isPinned, propIsPinned]);

  // Check login status and user activation status
  useEffect(() => {
    const checkLoginStatus = async () => {
      const username = localStorage.getItem('username');
      const token = localStorage.getItem('token');
      const status = localStorage.getItem('userStatus');
      
      if (username && token) {
        setIsLoggedIn(true);
        setCurrentUser(username);
        setUserStatus(status || 'pending');
        
        // Load user profile for display name (pass requester to avoid PII masking)
        try {
          const response = await api.get(`/profile/${username}?requester=${username}`);
          setUserProfile(response.data);
          // Extract role from profile (check both role_name and role fields)
          const role = response.data.role_name || response.data.role || 'free_user';
          setUserRole(role);
          localStorage.setItem('userRole', role); // Cache for quick access
        } catch (error) {
          console.error('Error loading user profile:', error);
          // Fallback to cached role if profile load fails
          const cachedRole = localStorage.getItem('userRole') || 'free_user';
          setUserRole(cachedRole);
        }
      } else {
        setIsLoggedIn(false);
        setCurrentUser(null);
        setUserProfile(null);
        setUserRole(null);
        setUserStatus('pending');
      }
    };

    checkLoginStatus();

    // Listen for login/logout events
    window.addEventListener('loginStatusChanged', checkLoginStatus);
    window.addEventListener('storage', checkLoginStatus);

    return () => {
      window.removeEventListener('loginStatusChanged', checkLoginStatus);
      window.removeEventListener('storage', checkLoginStatus);
    };
  }, []);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const username = localStorage.getItem('username');
        const response = await api.get(`/messages/unread-count/${username}`);
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    if (isLoggedIn) {
      fetchUnreadCount();
    }
  }, [isLoggedIn]);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLogout = async () => {
    const username = currentUser;
    
    // Disconnect WebSocket
    console.log('🔌 Disconnecting WebSocket');
    socketService.disconnect();
    
    // Mark user as offline (non-blocking beacon)
    if (username) {
      navigator.sendBeacon(
        `${getApiUrl()}/online-status/${username}/offline`,
        ''
      );
    }
    
    localStorage.removeItem('username');
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userStatus');
    localStorage.removeItem('appTheme'); // Clear theme cache
    setIsLoggedIn(false);
    setCurrentUser(null);
    setUserProfile(null);
    setUserRole(null);
    setUserStatus('pending');
    // Dispatch event to notify other components (TopBar, etc.)
    window.dispatchEvent(new Event('loginStatusChanged'));
    
    navigate('/');
  };

  // Helper function to handle menu item clicks
  const handleMenuClick = (action) => {
    if (action) {
      action(); // Execute the navigation action
    }
    // Auto-collapse to slim mode after clicking a menu item (only if not pinned)
    if (!isCollapsed && !isPinned) {
      onToggle();
    }
  };

  // Build menu items based on user role
  const buildMenuItems = () => {
    // Check if user is activated (admin and moderator roles are always active)
    const isAdmin = userRole === 'admin';
    const isModerator = userRole === 'moderator';
    const isActive = isAdmin || isModerator || userStatus === 'active';
    // Debug logging removed - uncomment if needed for debugging
    // console.log('🔍 Sidebar Debug:', { isLoggedIn, currentUser, userStatus, isActive });
    
    if (!isLoggedIn) {
      return [
        { 
          icon: '🔑', 
          label: 'Login', 
          action: handleLogin
        },
        { 
          icon: '�', 
          label: 'Register (Invitation Only)', 
          action: () => navigate('/register2')
        },
      ];
    }

    // isActive already declared above with debug logging

    const items = [
      { 
        icon: '🦋', 
        label: 'My Dashboard', 
        subLabel: 'Overview & Activity',
        action: () => navigate('/dashboardv2'),
        links: [
          {
            label: 'Legacy dashboard',
            action: () => navigate('/dashboard-legacy'),
          },
        ],
        disabled: !isActive
      },
      { 
        icon: '👤', 
        label: userProfile ? getShortName(userProfile) : (currentUser || 'Profile'), 
        subLabel: 'Profile data',
        action: () => navigate(`/profile/${currentUser}`),
        disabled: false, // Always enabled - users need to access their profile
        profileImage: true // Flag to render profile image instead of icon
      },
      { 
        icon: '🔍', 
        label: 'Search Profiles', 
        subLabel: 'Find matches',
        action: () => navigate('/search'),
        disabled: !isActive
      },
      {
        icon: '💬',
        label: 'My Messages',
        subLabel: 'Chat with matches',
        action: () => navigate('/messages'),
        disabled: !isActive,
        badge: unreadCount
      },
      { 
        icon: '🔒', 
        label: 'Contacts & Photo Requests', 
        subLabel: 'Manage contact & photo access',
        action: () => navigate('/pii-management'),
        disabled: !isActive
      },
      { 
        icon: '🤝', 
        label: 'Invite Friends', 
        subLabel: 'Share with friends & family',
        action: () => navigate('/invite-friends'),
        disabled: !isActive
      },
      { 
        icon: '📅', 
        label: 'Events', 
        subLabel: 'Match & connect',
        action: () => navigate('/l3v3l-meets'),
        disabled: !isActive
      },
      { 
        icon: '📝', 
        label: 'Blogs', 
        subLabel: 'Read our latest articles',
        action: () => navigate('/blog'),
        disabled: false
      },
    ];

    // Add Admin section for admin user (check role, not username)
    if (isAdmin) {
      // === CORE ADMIN SECTION ===
      items.push({ isHeader: true, label: 'ADMIN SECTION' });
      
      items.push({
        icon: '🔐',
        label: 'Admin Dashboard',
        subLabel: 'Manage all users',
        action: () => navigate('/admin')
      });

      // === UTILITIES ===
      // items.push({ isHeader: true, label: 'UTILITIES' });

      // items.push({
      //   icon: '🔧',
      //   label: 'Utilities',
      //   subLabel: 'Admin tools & utilities',
      //   action: () => navigate('/admin-utilities')
      // });

      // === MEMBER ACQUIRE ===
      items.push({ isHeader: true, label: 'MEMBER ACQUIRE' });
      
      items.push({
        icon: '📧',
        label: 'Invitations',
        subLabel: 'Manage user invitations',
        action: () => navigate('/invitations')
      });
      
      items.push({
        icon: '📋',
        label: 'Interests',
        subLabel: 'Review interest forms',
        action: () => navigate('/admin?tab=interests')
      });
      
      items.push({
        icon: '🏛️',
        label: 'Admin Hub',
        subLabel: 'Contributions, reports, utilities, marketing, blog & tests',
        action: () => navigate('/admin-hub?section=contributions'),
        divider: true
      });
      // === CONFIGURATION ===
      // items.push({ isHeader: true, label: 'CONFIGURATION' });

      items.push({
        icon: '⚙️',
        label: 'Settings',
        subLabel: 'App preferences',
        action: () => navigate('/preferences'),
        disabled: false // Always enabled - paused users need to access settings to unpause
      });
      
    }

    // Add Moderator section for moderator user
    if (isModerator) {
      items.push({ isHeader: true, label: 'MODERATOR SECTION' });
      
      items.push({
        icon: '🔐',
        label: 'Admin Dashboard',
        subLabel: 'Manage all users',
        action: () => navigate('/admin')
      });

      items.push({ isHeader: true, label: 'MONITORING' });
      
      items.push({
        icon: '📝',
        label: 'Blog Management',
        subLabel: 'Create & manage blog posts',
        action: () => navigate('/blog-management')
      });

      items.push({ isHeader: true, label: 'GROWTH' });
      
      items.push({
        icon: '📧',
        label: 'Invitations Manager',
        subLabel: 'Manage user invitations',
        action: () => navigate('/invitations')
      });
      
      items.push({
        icon: '📋',
        label: 'Registration Interests',
        subLabel: 'Review interest forms',
        action: () => navigate('/admin?tab=interests')
      });
      
      items.push({ isHeader: true, label: 'CONFIGURATION' });

      items.push({
        icon: '⚙️',
        label: 'Settings',
        subLabel: 'App preferences',
        action: () => navigate('/preferences'),
        disabled: false // Always enabled - paused users need to access settings to unpause
      });
    }

    // Show Settings for non-admin users (admins have it in their sections)
    if (isLoggedIn && userRole !== 'admin' && userRole !== 'moderator') {
      items.push({
        icon: '⚙️',
        label: 'Settings',
        subLabel: 'Preferences, Theme & Notifications',
        action: () => navigate('/preferences'),
        disabled: false // Always enabled - paused users need to access settings to unpause
      });
    }

    // Add Support before logout
    items.push({
      icon: '📧',
      label: 'Support',
      subLabel: 'Get help & contact support',
      action: () => navigate('/support')
    });

    // Add logout at the end
    items.push({ 
      icon: '🚪', 
      label: 'Logout', 
      action: handleLogout
    });

    return items;
  };

  const menuItems = buildMenuItems();
  // console.log('📋 Menu Items Count:', menuItems.length);

  const expanded = !isCollapsed;

  return (
    <>
      {/* Backdrop - Click outside to close expanded sidebar (only if not pinned) */}
      {expanded && !isPinned && (
        <div 
          className="sidebar-backdrop"
          onClick={() => onToggle()}
          aria-label="Close sidebar"
        />
      )}
      
      <div 
        className={`sidebar ${expanded ? 'expanded' : 'collapsed'}`}
      >
          {/* Header with toggle and pin buttons */}
          <div className="sidebar-header">
            {/* Toggle button */}
            <div className="sidebar-toggle-top" onClick={onToggle} title={expanded ? 'Close sidebar' : 'Open sidebar'}>
              <span className="sidebar-toggle-icon">{expanded ? '✕' : '☰'}</span>
            </div>
            
            {/* Pin button - only show when expanded */}
            {expanded && (
              <div 
                className="sidebar-pin-button"
                onClick={() => handlePinToggle(!isPinned)}
                title={isPinned ? 'Unpin sidebar - will auto-collapse' : 'Pin sidebar - stay open'}
              >
                <span className="pin-icon">{isPinned ? '📌' : '📍'}</span>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div className="sidebar-menu">
          {menuItems.length === 0 && expanded && (
            <div style={{padding: '20px', color: '#666'}}>
              No menu items available
            </div>
          )}
          {menuItems.map((item, index) => {
            // In collapsed mode, show section headers as thin divider lines
            if (isCollapsed && item.isHeader) {
              return <div key={index} className="menu-divider" />;
            }

            return (
              <React.Fragment key={index}>
                {item.divider && <div className="menu-divider" />}
                <div
                  className={`menu-item ${item.isHeader ? 'menu-header' : ''} ${item.disabled ? 'disabled' : ''}`}
                  onClick={item.disabled ? undefined : () => handleMenuClick(item.action)}
                  title={isCollapsed ? item.label : (item.disabled ? 'Please activate your account to access this feature' : '')}
                >
                {item.profileImage ? (
                  <div className="menu-icon profile-icon">
                    {getProfilePicUrl(userProfile) ? (
                      <img src={getProfilePicUrl(userProfile)} alt={currentUser} className="profile-avatar" />
                    ) : (
                      <div className="profile-avatar-placeholder">
                        {userProfile?.firstName?.[0] || currentUser?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    {item.badge && <span className="menu-badge">{item.badge}</span>}
                  </div>
                ) : (
                  <div className="menu-icon">
                    {item.icon}
                    {item.badge && <span className="menu-badge">{item.badge}</span>}
                  </div>
                )}
                {expanded && (
                  <div className="menu-content">
                    <div className="menu-label">{item.label}</div>
                    {item.subLabel && (
                      <div className="menu-sublabel">{item.subLabel}</div>
                    )}
                    {Array.isArray(item.links) && item.links.length > 0 && (
                      <div className="menu-links">
                        {item.links.map((link, i) => (
                          <button
                            key={`${item.label}-link-${i}`}
                            type="button"
                            className="menu-sublabel menu-sublabel-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuClick(link.action);
                            }}
                          >
                            {link.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {expanded && item.disabled && (
                  <div className="disabled-badge">🔒</div>
                )}
              </div>
              {item.divider && <div className="menu-divider" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Footer Links - only in expanded mode */}
        {expanded && (
          <div className="sidebar-footer">
            <span className="footer-link" onClick={() => handleMenuClick(() => navigate('/help'))}>📚 Help</span>
            <span className="footer-separator">|</span>
            <span className="footer-link" onClick={() => handleMenuClick(() => navigate('/l3v3l-info'))}>🦋 L3V3L</span>
            <span className="footer-separator">|</span>
            <span className="footer-link" onClick={() => handleMenuClick(() => navigate('/privacy'))}>Privacy</span>
            <span className="footer-separator">|</span>
            <span className="footer-link" onClick={() => handleMenuClick(() => navigate('/about'))}>About Us</span>
            <span className="footer-separator">|</span>
            <span className="footer-link" onClick={() => handleMenuClick(() => navigate('/trademark'))}>Trademark</span>
            <span className="footer-separator">|</span>
            <span className="footer-link" onClick={() => handleMenuClick(() => navigate('/testimonials'))}>💬 Testimonials</span>
            <span className="footer-separator">|</span>
            <span className="footer-link" onClick={() => handleMenuClick(() => navigate('/contact'))}>📧 Contact Us</span>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
